import { useState, useRef, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Search, SlidersHorizontal, AlertCircle, Megaphone, ChevronDown, Check } from 'lucide-react';
import { useGetAdsQuery, useGetCategoryTreeQuery, useGetCitiesQuery } from '@nimbazaar/store';
import { AdCard, apiAdToCardProps } from '../components/AdCard';
import { Pagination } from '../components/Pagination';

const SORT_OPTIONS = [
  { value: 'newest', label: 'Newest First', sortBy: 'createdAt', sortOrder: 'desc' },
  { value: 'oldest', label: 'Oldest First', sortBy: 'createdAt', sortOrder: 'asc' },
  { value: 'most-viewed', label: 'Most Viewed', sortBy: 'viewCount', sortOrder: 'desc' },
  { value: 'most-bids', label: 'Most Bids', sortBy: 'bidCount', sortOrder: 'desc' },
];

interface DropdownOption {
  value: string;
  label: string;
  isChild?: boolean;
}

function FilterDropdown({
  options,
  value,
  onChange,
  placeholder,
}: {
  options: DropdownOption[];
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setIsOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const selected = options.find((o) => o.value === value);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`w-full flex items-center justify-between px-3 py-2.5 border rounded-lg text-sm transition-all ${
          isOpen
            ? 'border-primary-500 ring-2 ring-primary-500'
            : 'border-gray-200 hover:border-gray-300'
        } bg-white`}
      >
        <span className={selected ? 'text-gray-900' : 'text-gray-400'}>{selected?.label || placeholder}</span>
        <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden">
          <div className="max-h-64 overflow-y-auto py-1">
            {options.map((opt) => {
              const isActive = opt.value === value;
              return (
                <button
                  key={opt.value}
                  onClick={() => {
                    onChange(opt.value);
                    setIsOpen(false);
                  }}
                  className={`w-full text-left py-2 text-sm transition-colors flex items-center gap-2 ${
                    isActive
                      ? 'bg-primary-50 text-primary-700 font-medium'
                      : 'text-gray-700 hover:bg-gray-50'
                  } ${opt.isChild ? 'pl-8' : 'pl-3'}`}
                >
                  {isActive ? (
                    <Check className="w-3.5 h-3.5 text-primary-500 shrink-0" />
                  ) : (
                    <span className="w-3.5 shrink-0" />
                  )}
                  <span>{opt.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function LoadingSkeleton() {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2 sm:gap-4">
      {Array.from({ length: 8 }).map((_, i) => (
        <div
          key={i}
          className="bg-white rounded-xl shadow-md overflow-hidden animate-pulse"
        >
          <div className="h-36 sm:h-44 bg-gray-200" />
          <div className="p-4 space-y-3">
            <div className="h-4 bg-gray-200 rounded w-3/4" />
            <div className="h-5 bg-gray-200 rounded w-1/2" />
            <div className="h-3 bg-gray-200 rounded w-1/3" />
            <div className="flex justify-between pt-3 border-t border-gray-100">
              <div className="h-3 bg-gray-200 rounded w-16" />
              <div className="h-3 bg-gray-200 rounded w-12" />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

export function AdsListingPage() {
  const [searchParams, setSearchParams] = useSearchParams();

  const search = searchParams.get('search') || '';
  const categoryId = searchParams.get('categoryId') || '';
  const location = searchParams.get('location') || '';
  const sort = searchParams.get('sort') || 'newest';
  const verifiedOnly = searchParams.get('verified') === 'true';
  const page = parseInt(searchParams.get('page') || '1', 10);

  const sortOption = SORT_OPTIONS.find((o) => o.value === sort) || SORT_OPTIONS[0];

  const { data, isLoading, isError, refetch } = useGetAdsQuery({
    page,
    limit: 20,
    search: search || undefined,
    categoryId: categoryId || undefined,
    location: location || undefined,
    sortBy: sortOption.sortBy,
    sortOrder: sortOption.sortOrder,
  });

  const { data: treeData } = useGetCategoryTreeQuery();
  const { data: citiesData } = useGetCitiesQuery();
  const categories = treeData?.data ?? [];
  const cities = citiesData?.data ?? [];

  const allAds = data?.data ?? [];
  const ads = verifiedOnly
    ? allAds.filter((ad) => (ad as { user?: { emailVerified?: boolean } }).user?.emailVerified)
    : allAds;
  const meta = data?.meta;

  const updateParam = (key: string, value: string) => {
    const next = new URLSearchParams(searchParams);
    if (value) {
      next.set(key, value);
    } else {
      next.delete(key);
    }
    // Reset to page 1 when filters change (except when changing page itself)
    if (key !== 'page') {
      next.delete('page');
    }
    setSearchParams(next);
  };

  // Build a flat list of all categories (parent + children) for the dropdown
  const categoryOptions: DropdownOption[] = [
    { value: '', label: 'All Categories' },
    ...categories.flatMap((cat) => [
      { value: cat.id, label: cat.name },
      ...cat.children.map((sub) => ({ value: sub.id, label: sub.name, isChild: true })),
    ]),
  ];

  const sortDropdownOptions: DropdownOption[] = SORT_OPTIONS.map((o) => ({
    value: o.value,
    label: o.label,
  }));

  return (
    <div className="bg-gray-50 min-h-screen">
      <div className="max-w-7xl mx-auto px-3 sm:px-4 py-4 sm:py-6">
        {/* Header */}
        <div className="mb-4 sm:mb-6">
          <h1 className="text-lg sm:text-2xl font-bold text-gray-900">
            Browse Wanted Ads
          </h1>
          {meta && (
            <p className="text-sm text-gray-500 mt-1">
              {meta.total} {meta.total === 1 ? 'ad' : 'ads'} found
            </p>
          )}
        </div>

        {/* Filters Bar */}
        <div className="bg-white rounded-xl shadow-sm p-3 sm:p-4 mb-4 sm:mb-6">
          <div className="flex items-center gap-2 mb-2 sm:mb-3 text-sm font-medium text-gray-700">
            <SlidersHorizontal className="w-4 h-4" />
            Filters
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-3">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search ads..."
                defaultValue={search}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    updateParam('search', (e.target as HTMLInputElement).value);
                  }
                }}
                onBlur={(e) => updateParam('search', e.target.value)}
                className="w-full pl-9 pr-3 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
              />
            </div>

            {/* Category */}
            <FilterDropdown
              options={categoryOptions}
              value={categoryId}
              onChange={(v) => updateParam('categoryId', v)}
              placeholder="All Categories"
            />

            {/* Location */}
            <FilterDropdown
              options={[
                { value: '', label: 'All Locations' },
                ...cities.map((c) => ({ value: c.name, label: c.name })),
              ]}
              value={location}
              onChange={(v) => updateParam('location', v)}
              placeholder="All Locations"
            />

            {/* Sort */}
            <FilterDropdown
              options={sortDropdownOptions}
              value={sort}
              onChange={(v) => updateParam('sort', v)}
              placeholder="Sort by"
            />
          </div>

          {/* Verified toggle */}
          <div className="flex items-center gap-2 mt-3 pt-3 border-t border-gray-100">
            <button
              onClick={() => updateParam('verified', verifiedOnly ? '' : 'true')}
              className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                verifiedOnly ? 'bg-primary-500' : 'bg-gray-300'
              }`}
            >
              <span
                className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${
                  verifiedOnly ? 'translate-x-[18px]' : 'translate-x-1'
                }`}
              />
            </button>
            <span className="text-sm text-gray-600">Ads from verified buyers only</span>
          </div>
        </div>

        {/* Results */}
        {isLoading ? (
          <LoadingSkeleton />
        ) : isError ? (
          <div className="text-center py-16">
            <AlertCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-gray-900 mb-2">Something went wrong</h2>
            <p className="text-gray-600 mb-6">Failed to load ads. Please try again.</p>
            <button
              onClick={refetch}
              className="px-6 py-2 bg-primary-500 hover:bg-primary-600 text-white font-medium rounded-lg transition-colors"
            >
              Try Again
            </button>
          </div>
        ) : ads.length === 0 ? (
          <div className="text-center py-16">
            <div className="w-16 h-16 sm:w-20 sm:h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Megaphone className="w-10 h-10 text-gray-300" />
            </div>
            <h2 className="text-xl font-bold text-gray-900 mb-2">No ads found</h2>
            <p className="text-gray-500">
              {search || categoryId || location
                ? 'Try adjusting your filters to see more results.'
                : 'There are no ads posted yet.'}
            </p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2 sm:gap-4">
              {ads.map((ad) => {
                const props = apiAdToCardProps(ad);
                return <AdCard key={ad.id} {...props} fullWidth />;
              })}
            </div>

            {/* Pagination */}
            {meta && (
              <div className="mt-8">
                <Pagination
                  page={meta.page}
                  totalPages={meta.totalPages}
                  hasNextPage={meta.hasNextPage}
                  hasPrevPage={meta.hasPrevPage}
                  onPageChange={(p) => updateParam('page', String(p))}
                />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
